// src/services/monitoringService.js
if (process.env.NODE_ENV !== 'production') console.log('MonitoringService file loaded');

class MonitoringService {
  constructor() {
    this.metrics = {
      pageViews: new Map(),
      apiCalls: new Map(),
      errors: new Map(),
      userInteractions: new Map(),
      performanceMetrics: []
    };
    
    this.startTime = Date.now();
    this.isEnabled = true; // Always enabled for development/testing
    
    if (process.env.NODE_ENV !== 'production') console.log('MonitoringService initialized');
    
    // Always setup handlers
    this.initializeGlobalHandlers();
    this.startPerformanceMonitoring();
    
    // Make globally available for testing
    if (typeof window !== 'undefined') {
      window.monitoringService = this;
      if (process.env.NODE_ENV !== 'production') console.log('MonitoringService available globally as window.monitoringService');
    }
  }

  // Sayfa görüntüleme tracking
  trackPageView(pageName) {
    const currentCount = this.metrics.pageViews.get(pageName) || 0;
    this.metrics.pageViews.set(pageName, currentCount + 1);
    if (process.env.NODE_ENV !== 'production') console.log(`Page view tracked: ${pageName} (count: ${currentCount + 1})`);
  }

  // API çağrısı tracking
  trackApiCall(endpoint, method, status, duration = 0) {
    const key = `${method}_${endpoint}_${status}`;
    const currentCount = this.metrics.apiCalls.get(key) || 0;
    this.metrics.apiCalls.set(key, currentCount + 1);
    
    // Performance data olarak kaydet
    this.metrics.performanceMetrics.push({
      type: 'api_call',
      endpoint,
      method,
      status,
      duration,
      timestamp: Date.now()
    });
    
    // Son 100 performance metric'i tut
    if (this.metrics.performanceMetrics.length > 100) {
      this.metrics.performanceMetrics = this.metrics.performanceMetrics.slice(-100);
    }
    
    if (process.env.NODE_ENV !== 'production') console.log(`API call tracked: ${method} ${endpoint} - ${status} (${duration}ms)`);
  }

  // Hata tracking
  trackError(errorType, errorMessage, stackTrace = '') {
    const key = `${errorType}_${errorMessage}`;
    const currentCount = this.metrics.errors.get(key) || 0;
    this.metrics.errors.set(key, currentCount + 1);
    
    if (process.env.NODE_ENV !== 'production') console.error(`Error tracked: ${errorType} - ${errorMessage}`);
    
    // Performance data olarak da kaydet
    this.metrics.performanceMetrics.push({
      type: 'error',
      errorType,
      errorMessage,
      stackTrace,
      timestamp: Date.now()
    });
  }

  // Kullanıcı etkileşimi tracking
  trackUserInteraction(action, component) {
    const key = `${component}_${action}`;
    const currentCount = this.metrics.userInteractions.get(key) || 0;
    this.metrics.userInteractions.set(key, currentCount + 1);
    
    if (process.env.NODE_ENV !== 'production') console.log(`User interaction tracked: ${action} on ${component} (count: ${currentCount + 1})`);
  }

  // Global error handler setup
  initializeGlobalHandlers() {
    // JavaScript errors
    window.addEventListener('error', (event) => {
      this.trackError('javascript_error', event.message, event.error?.stack);
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError('promise_rejection', event.reason?.message || event.reason?.toString(), event.reason?.stack);
    });

    // Page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.trackUserInteraction('page_visible', 'browser');
      } else {
        this.trackUserInteraction('page_hidden', 'browser');
      }
    });

    if (process.env.NODE_ENV !== 'production') console.log('Global error handlers initialized');
  }

  // Performance monitoring setup
  startPerformanceMonitoring() {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        // LCP (Largest Contentful Paint)
        new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            this.metrics.performanceMetrics.push({
              type: 'lcp',
              value: entry.startTime,
              timestamp: Date.now()
            });
            console.log(`LCP recorded: ${entry.startTime.toFixed(2)}ms`);
          }
        }).observe({ type: 'largest-contentful-paint', buffered: true });

        // FID (First Input Delay)
        new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            const delay = entry.processingStart - entry.startTime;
            this.metrics.performanceMetrics.push({
              type: 'fid',
              value: delay,
              timestamp: Date.now()
            });
            console.log(`FID recorded: ${delay.toFixed(2)}ms`);
          }
        }).observe({ type: 'first-input', buffered: true });

        if (process.env.NODE_ENV !== 'production') console.log('Performance monitoring initialized');
      } catch (error) {
        console.warn('Performance monitoring setup failed:', error);
      }
    } else {
      if (process.env.NODE_ENV !== 'production') console.log('PerformanceObserver not available');
    }
  }

  // Dashboard için özet metrics
  getSummaryMetrics() {
    const summary = {
      totalPageViews: Array.from(this.metrics.pageViews.values()).reduce((a, b) => a + b, 0),
      totalApiCalls: Array.from(this.metrics.apiCalls.values()).reduce((a, b) => a + b, 0),
      totalErrors: Array.from(this.metrics.errors.values()).reduce((a, b) => a + b, 0),
      totalUserInteractions: Array.from(this.metrics.userInteractions.values()).reduce((a, b) => a + b, 0),
      uptime: Math.floor((Date.now() - this.startTime) / 1000)
    };
    
    if (process.env.NODE_ENV !== 'production') console.log('Summary metrics generated:', summary);
    return summary;
  }

  // Detaylı metrics (JSON format)
  getDetailedMetrics() {
    return {
      pageViews: Object.fromEntries(this.metrics.pageViews),
      apiCalls: Object.fromEntries(this.metrics.apiCalls),
      errors: Object.fromEntries(this.metrics.errors),
      userInteractions: Object.fromEntries(this.metrics.userInteractions),
      performanceMetrics: this.metrics.performanceMetrics.slice(-20), // Son 20 metric
      uptime: Math.floor((Date.now() - this.startTime) / 1000),
      timestamp: new Date().toISOString()
    };
  }

  // Prometheus formatında metrics
  getPrometheusMetrics() {
    let output = '';

    // Page views
    output += '# HELP tegim_page_views_total Total page views by page\n';
    output += '# TYPE tegim_page_views_total counter\n';
    for (const [page, count] of this.metrics.pageViews) {
      output += `tegim_page_views_total{page="${page}"} ${count}\n`;
    }
    output += '\n';

    // API calls
    output += '# HELP tegim_api_calls_total Total API calls by method, endpoint, and status\n';
    output += '# TYPE tegim_api_calls_total counter\n';
    for (const [key, count] of this.metrics.apiCalls) {
      const parts = key.split('_');
      const method = parts[0];
      const status = parts[parts.length - 1];
      const endpoint = parts.slice(1, -1).join('_');
      output += `tegim_api_calls_total{method="${method}",endpoint="${endpoint}",status="${status}"} ${count}\n`;
    }
    output += '\n';

    // Errors
    output += '# HELP tegim_errors_total Total errors by type and message\n';
    output += '# TYPE tegim_errors_total counter\n';
    for (const [errorKey, count] of this.metrics.errors) {
      const cleanErrorKey = errorKey.replace(/"/g, '\\"'); // Escape quotes
      output += `tegim_errors_total{error="${cleanErrorKey}"} ${count}\n`;
    }
    output += '\n';

    // User interactions
    output += '# HELP tegim_user_interactions_total Total user interactions by component and action\n';
    output += '# TYPE tegim_user_interactions_total counter\n';
    for (const [interactionKey, count] of this.metrics.userInteractions) {
      const parts = interactionKey.split('_');
      const component = parts[0];
      const action = parts.slice(1).join('_');
      output += `tegim_user_interactions_total{component="${component}",action="${action}"} ${count}\n`;
    }
    output += '\n';

    // App uptime
    const uptime = Math.floor((Date.now() - this.startTime) / 1000);
    output += '# HELP tegim_app_uptime_seconds Application uptime in seconds\n';
    output += '# TYPE tegim_app_uptime_seconds gauge\n';
    output += `tegim_app_uptime_seconds ${uptime}\n\n`;

    // Performance metrics summary
    const avgLCP = this.getAverageMetric('lcp');
    const avgFID = this.getAverageMetric('fid');
    
    if (avgLCP > 0) {
      output += '# HELP tegim_lcp_average_ms Average Largest Contentful Paint in milliseconds\n';
      output += '# TYPE tegim_lcp_average_ms gauge\n';
      output += `tegim_lcp_average_ms ${avgLCP.toFixed(2)}\n\n`;
    }

    if (avgFID > 0) {
      output += '# HELP tegim_fid_average_ms Average First Input Delay in milliseconds\n';
      output += '# TYPE tegim_fid_average_ms gauge\n';
      output += `tegim_fid_average_ms ${avgFID.toFixed(2)}\n\n`;
    }

    if (process.env.NODE_ENV !== 'production') console.log('Prometheus metrics generated:', output.length, 'characters');
    return output;
  }

  // Helper: Calculate average for performance metrics
  getAverageMetric(type) {
    const metrics = this.metrics.performanceMetrics.filter(m => m.type === type);
    if (metrics.length === 0) return 0;
    const sum = metrics.reduce((acc, m) => acc + m.value, 0);
    return sum / metrics.length;
  }

  // Debug: Log all current metrics
  debugLogMetrics() {
    if (process.env.NODE_ENV !== 'production') {
      console.group('Current Metrics Debug');
      console.log('Page Views:', Object.fromEntries(this.metrics.pageViews));
      console.log('API Calls:', Object.fromEntries(this.metrics.apiCalls));
      console.log('⚠️ Errors:', Object.fromEntries(this.metrics.errors));
      console.log('User Interactions:', Object.fromEntries(this.metrics.userInteractions));
      console.log('⚡ Performance Metrics:', this.metrics.performanceMetrics.slice(-5));
      console.log('Summary:', this.getSummaryMetrics());
      console.groupEnd();
    }
  }

  // Not: test helper kaldırıldı (mock veri devre dışı)
}

// Create singleton instance
const monitoringService = new MonitoringService();

export default monitoringService;
